import { Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import CustomWeb from "@components/atoms/Webview";
import Footer from "@scenes/pdp/utils/Footer";
import { Diamond } from "@src/types/DiamondDetail";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Platform, StyleSheet, View } from "react-native";
import styled from "styled-components/native";
import EmptyImagePreview from "../GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "../GalleryView/utils/EmptyImage/utils";
import { GalleryCategory } from "../GalleryView/utils/GalleryType";
import OpenReportView from "../GalleryView/utils/OpenReport/OpenReportView";
import { ImageObject } from "../GalleryView/utils/types";
import GalleryIconsView from "./GalleryIconsMatchView";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import {
  Image as ExpoImage,
  ImageContentFit,
  ImageLoadEventData,
} from "expo-image";
import { useFindMatch } from "@scenes/findMatch/FindMatchContext";
import { isJPGUrl, isPdfUrl } from "../GalleryView/utils/helpers";
import {
  getImageSource,
  getIOSImageProps,
  getOptimizedImageUrl,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { isPair } from "@utils/product/productValidators";
import {
  getResizeModeFromDimensions,
  ImageDimensions,
} from "@utils/imageAspectRatio";
import { MediaType } from "@utils/constants";
import { useSelector } from "react-redux";

type GalleryViewModalProps = {
  ReceivedMedia: any;
  initialIndex?: number;
  image_thumb_url?: string;
  productType?: string;
  itemDetail?: Diamond;
  handleOpenExpandModel?: any;
  imageWidth?: number;
  testIDPrefix?: string;
};

const GalleryViewMatch = ({
  ReceivedMedia,
  initialIndex = 0,
  itemDetail,
  handleOpenExpandModel,
  imageWidth,
  testIDPrefix = "findMatch.gallery",
}: GalleryViewModalProps) => {
  const { theme, productType } = useFindMatch();
  let thumbSize = 56;
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const ImageComponent = ExpoImage;
  const { deviceWidth: screenWidth } = useIsFoldable();
  const enableImageOptimizer = useSelector(
    (state: any) => state.globalData?.app_settings?.enable_image_optimizer
  );
  const [imageDimensions, setImageDimensions] =
    useState<ImageDimensions | null>(null);
  useEffect(() => {
    setActiveIndex(initialIndex);
  }, [initialIndex]);

  const leftItemLeftClickEvent = () => {
    if (activeIndex !== 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const leftItemRightClickEvent = () => {
    if (activeIndex < ReceivedMedia?.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const [imageError, setImageError] = useState(
    ReceivedMedia?.[activeIndex]?.link === "" ? true : false
  );

  useEffect(() => {
    setActiveIndex(0);
    setImageError(ReceivedMedia?.[activeIndex]?.link === "" ? true : false);
  }, [ReceivedMedia]);

  const RenderFooterComponent = ({
    currentIndex,
  }: {
    image: ImageObject;
    currentIndex: number;
  }) => {
    return (
      <>
        <Spacer size={SpaceType.Double} />
        <Footer
          hasArrow={true}
          centerString={"of"}
          total={ReceivedMedia?.length === 0 ? 1 : ReceivedMedia?.length}
          currentIndex={currentIndex}
          leftClickEvent={leftItemLeftClickEvent}
          rightClickEvent={leftItemRightClickEvent}
        />
      </>
    );
  };

  let isSmallDeviceWidth = true;
  const [deviceWidth] = useState(screenWidth);
  let resizeMode = "cover";
  let applyRadius = false;
  let resizeImage = false;
  let extraSmallImage = false;
  let isExternalView = GalleryCategory.ModelView;
  let showMediaModel = false;
  let GIACertEnable = true;

  const preFix =
    Platform.OS === "android"
      ? "https://docs.google.com/gview?embedded=true&url="
      : "";
  const loadingUrlExternalView =
    ReceivedMedia?.[activeIndex]?.type === "pdf" &&
    (isPdfUrl(ReceivedMedia?.[activeIndex]?.link) || showMediaModel)
      ? true
      : false;

  const injectedCSS = `
      .divbtnpos,.hPHUXe, .sliderHeight {
        display: none !important;
      }
    `;

  const dynamicResizeMode: ImageContentFit = useMemo(() => {
    const isImageType =
      ReceivedMedia?.[activeIndex]?.type === MediaType.IMAGE ||
      isJPGUrl(ReceivedMedia?.[activeIndex]?.link);
    if (!isImageType) {
      return resizeMode ?? "cover";
    }
    if (
      isJPGUrl(ReceivedMedia?.[activeIndex]?.link) &&
      ReceivedMedia?.[activeIndex]?.type === MediaType.PDF
    ) {
      return "contain";
    }
    return enableImageOptimizer
      ? getResizeModeFromDimensions(
          imageDimensions,
          resizeMode ?? "cover",
          theme
        )
      : resizeMode ?? "cover";
  }, [
    ReceivedMedia?.[activeIndex]?.type,
    ReceivedMedia?.[activeIndex]?.link,
    imageDimensions,
    resizeMode,
  ]);

  const handleImageLoad = useCallback((event: ImageLoadEventData) => {
    const { width: mWidth, height: mHeight } = event.source;
    if (mWidth && mHeight) {
      setImageDimensions({ width: mWidth, height: mHeight });
    }
  }, []);

  const imageComponent = useMemo(() => {
    if (ReceivedMedia?.length === 0) {
      return (
        <EmptyImagePreview
          shape={getShapeValue(itemDetail)}
          size={100}
          extraSmallImage={false}
          isExternalView={isExternalView}
          containerSize={imageWidth}
          isPair={isPair(itemDetail) ?? false}
        />
      );
    }

    let isJPGImage = isJPGUrl(ReceivedMedia?.[activeIndex]?.link);

    return ReceivedMedia?.[activeIndex]?.type === "image" || isJPGImage ? (
      !imageError ? (
        <ImageComponent
          contentFit={
            (ReceivedMedia?.[activeIndex]?.link
              ? isJPGImage && ReceivedMedia?.[activeIndex]?.type === "pdf"
                ? "contain"
                : dynamicResizeMode
              : "contain") as
              | "cover"
              | "contain"
              | "fill"
              | "none"
              | "scale-down"
          }
          source={getImageSource(
            getOptimizedImageUrl(
              ReceivedMedia?.[activeIndex]?.link,
              ImageSizePreset.PDP_MAIN
            ),
            ImageSizePreset.PDP_MAIN
          )}
          {...getIOSImageProps()}
          onError={() => setImageError(true)}
          onLoad={handleImageLoad}
          {...(enableImageOptimizer &&
            theme.isLargeScreen && { contentPosition: "top center" })}
          style={[
            isSmallDeviceWidth
              ? styles.smallimage(deviceWidth, applyRadius, resizeImage, false)
              : null,
            resizeImage
              ? !theme.isMobile
                ? styles.smallimage(600, applyRadius, resizeImage, true)
                : styles.galleryimage
              : extraSmallImage
              ? undefined
              : styles.largeimage,
            {
              backgroundColor: theme.isLargeScreen
                ? theme["core-02"]
                : theme["core-01"],
            },
          ]}
        />
      ) : (
        <EmptyImagePreview
          shape={getShapeValue(itemDetail)}
          size={100}
          extraSmallImage={true}
          isExternalView={isExternalView}
          containerSize={imageWidth}
          isPair={isPair(itemDetail) ?? false}
        />
      )
    ) : ReceivedMedia?.[activeIndex]?.type === "video" ? (
      <VideoContainer>
        <PlayContainer thumbSize={thumbSize}>
          <VDBIcon
            name={"play"}
            size={24}
            color={theme["core-01"]}
            variant={IconsVariant.VDBCustom}
          />
        </PlayContainer>
      </VideoContainer>
    ) : loadingUrlExternalView ? (
      <OpenReportView
        resizeImage={extraSmallImage}
        url={ReceivedMedia?.[activeIndex]?.link}
        showMediaModel={showMediaModel}
        mediaType={ReceivedMedia?.[activeIndex]?.type}
        itemID={itemDetail?.id}
        certNum={itemDetail?.cert_num}
        GIACertEnable={GIACertEnable}
        linkedPairChildren={itemDetail?.children}
      />
    ) : (
      <View
        style={{
          width: imageWidth,
          height: "100%",
        }}
      >
        <CustomWeb
          isSmallDeviceWidth={isSmallDeviceWidth}
          deviceWidth={200}
          resizeImage={resizeImage}
          url={
            ReceivedMedia?.[activeIndex]?.type === "pdf"
              ? preFix + ReceivedMedia?.[activeIndex]?.link
              : ReceivedMedia?.[activeIndex]?.link
          }
          isFromPDP={false}
          extraSmallImage={extraSmallImage}
          injectedCSS={injectedCSS}
          enableReload={ReceivedMedia?.[activeIndex]?.type === "pdf"}
          allowScaleScript={false}
          mediaType={ReceivedMedia?.[activeIndex]?.type}
        />
      </View>
    );
  }, [
    ReceivedMedia?.[activeIndex]?.link,
    ReceivedMedia?.[activeIndex]?.type,
    dynamicResizeMode,
    imageError,
    setImageError,
    handleImageLoad,
  ]);

  return (
    <Container testID={testIDPrefix} imageWidth={imageWidth}>
      <GalleryIconsView
        isOpen={false}
        productType={productType}
        itemDetail={itemDetail}
        fromPDPScreen={false}
        isSmallDeviceWidth={true}
        handleOpenExpandModel={handleOpenExpandModel}
        mediaUrl={ReceivedMedia[activeIndex]?.link}
      />
      <ImageContainer>
        <MainContainer
          type={ReceivedMedia?.[activeIndex]?.type}
          imageWidth={imageWidth}
        >
          {imageComponent}
        </MainContainer>
      </ImageContainer>

      <CounterContainer>
        <RenderFooterComponent
          image={ReceivedMedia}
          currentIndex={activeIndex}
        />
      </CounterContainer>
    </Container>
  );
};

const styles = StyleSheet.create({
  containerStyle: {
    height: "100%",
    width: "100%",
  },
  centercontainerStyle: {
    height: "100%",
    width: "100%",
    alignItems: "center",
  },
  largeimage: {
    height: "100%",
    width: "100%",
  },
  tabletContainerStyle: {
    height: "100%",
    width: "100%",
  },
  galleryimage: {
    justifyContent: "center",
    alignItems: "center",
  },
  smallimage: (
    deviceWidth: number,
    applyRadius: boolean,
    resizeImage: boolean,
    applyPercent: boolean
  ) => ({
    height: applyPercent ? "100%" : resizeImage ? deviceWidth / 2 : deviceWidth,
    width: applyPercent ? "100%" : resizeImage ? deviceWidth / 2 : deviceWidth,
    borderRadius: applyRadius ? 12 : 0,
    paddingBottom: "20px",
    overflow: "hidden",
  }),
  header: {
    position: "absolute",
    top: 0,
    width: "100%",
  },
  footer: {
    bottom: 0,
    position: "absolute",
    width: "100%",
  },
  activeThumb: {
    borderWidth: 3,
  },
  thumb: {
    borderRadius: 8,
    marginRight: 10,
    borderWidth: 1,
  },
  thumbnailListContainer: {
    paddingHorizontal: 10,
  },
  bottomFlatlist: {
    position: "absolute",
  },
});
const MainContainer = styled.View<any>`
  height: ${({ imageWidth }) => imageWidth}px;
  width: ${({ imageWidth }) => imageWidth}px;
  background-color: ${({ theme }) => theme["core-03"]};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const Container = styled.View<any>`
  height: ${({ imageWidth }) => imageWidth + 76}px;
`;

const ImageContainer = styled.View`
  align-items: center;
  flex-direction: column;
  flex: 1;
  flex-basis: auto;
`;

const CounterContainer = styled.View`
  align-items: center;
`;

const VideoContainer = styled.View<any>`
  border-radius: 12px;
  position: absolute;
  align: center;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const PlayContainer = styled.View<any>`
  border-radius: 30px;
  position: absolute;
  align: center;
  align-items: center;
  justify-content: center;
  height: ${({ thumbSize }) => thumbSize}px;
  width: ${({ thumbSize }) => thumbSize}px;
  background-color: ${({ theme }) => theme["over-02"]};
`;

export default GalleryViewMatch;
